import type { Dispatch, SetStateAction } from 'react';
import type { WalletContextState } from '@solana/wallet-adapter-react';
import type { NavigateFunction } from 'react-router';
import { toast } from 'sonner';
import type { Dapp } from '@src/dto';
import type { TRPC } from '../../../contexts/TRPC';
import { formatMintingErrorMessage } from '../../../utils/errorHandling';
import { captureAndReportError } from '../../../utils/errorReporting';
import type { DappRouteDapp } from '../../../utils/dappRoutes';
import { submitPublicationUpdate } from '../../../utils/publicationWorkflow';
import type { PublicationDraft, ProcessingStatus, UploadState } from '../types';
import { scheduleReleaseNavigation } from '../utils/releaseRoutes';

interface UseNewVersionSubmissionParams {
  dappData: Dapp | null;
  dappId: string | undefined;
  dappRouteDapp: DappRouteDapp | undefined;
  markProcessingStart: () => void;
  navigate: NavigateFunction;
  wallet: WalletContextState;
  resetProcessingFlow: () => void;
  setIsSubmitting: (value: boolean) => void;
  setProcessingStatus: Dispatch<SetStateAction<ProcessingStatus>>;
  setUploadState: (value: UploadState) => void;
  trpc: TRPC['client'];
}

export function useNewVersionSubmission({
  dappData,
  dappId,
  dappRouteDapp,
  markProcessingStart,
  navigate,
  wallet,
  resetProcessingFlow,
  setIsSubmitting,
  setProcessingStatus,
  setUploadState,
  trpc,
}: UseNewVersionSubmissionParams) {
  const setErrorState = (errorMessage: string) => {
    setProcessingStatus({
      error: errorMessage,
      phase: undefined,
      jobId: undefined,
      releaseData: undefined,
      publicationSessionId: undefined,
      releaseId: undefined,
      transactionSignature: undefined,
      mintAddress: undefined,
      progress: undefined,
      status: undefined,
      message: undefined,
    });
    setUploadState('error');
    setIsSubmitting(false);
  };

  const handleSubmit = async ({ source, whatsNew }: PublicationDraft) => {
    if (!dappId) {
      toast.error('No dApp selected');
      return;
    }

    if (!dappData) {
      toast.error('App data is still loading. Please try again.');
      return;
    }

    setIsSubmitting(true);
    resetProcessingFlow();
    setUploadState('uploading');
    markProcessingStart();

    try {
      const submissionResult = await submitPublicationUpdate({
        trpc,
        wallet,
        dappId,
        whatsNew,
        source,
        onProgress: (state) => {
          setProcessingStatus((prev) => ({
            ...prev,
            ...state,
            releaseId: state.releaseId || prev.releaseId,
          }));
        },
      });

      setProcessingStatus((prev) => ({
        ...prev,
        releaseId: submissionResult.releaseId,
        jobId: undefined,
        releaseData: undefined,
        ingestionSessionId: submissionResult.ingestionSessionId,
        publicationSessionId: submissionResult.publicationSessionId,
        mintAddress: submissionResult.mintAddress,
        transactionSignature: submissionResult.transactionSignature,
        progress: 100,
        phase: 'done',
        status: 'Published',
        error: undefined,
        message: 'Release published successfully',
      }));

      toast.success('Release published successfully', {
        description: 'The portal finished the update flow.',
      });

      setIsSubmitting(false);
      resetProcessingFlow();

      if (submissionResult.releaseId) {
        scheduleReleaseNavigation(
          navigate,
          dappRouteDapp,
          submissionResult.releaseId,
        );
      }
    } catch (error) {
      captureAndReportError('Error publishing release:', error);

      const errorMessage = formatMintingErrorMessage(error);

      toast.error('Failed to publish the release', {
        description: errorMessage,
        duration: 5000,
      });

      setErrorState(errorMessage);
    }
  };

  return {
    handleSubmit,
  };
}
